import { getClassroomApiUrl } from "@/js/utils/getBackendUrl";
import * as clsApi from "@/js/api/classroomApi";
import { NativeAppControlRule } from "@/js/api/classroomPolicyApi";

/**
 * App controls are not classroom rules. They predate the rules library and live in their own
 * `cds.app_controls` table behind their own `/v1/devices/{deviceId}/classrooms/...` endpoints,
 * never reaching classroom-policy-service. That is why they cannot be sent to the classroom-rules
 * endpoints in `@/api/classroomRules`, whose `type` accepts only `URL` and `SIGNATURE`.
 *
 * The storage shape differs too: one row per student (whole-class rules use an empty
 * `studentStableIdV1`), a boolean action, and no enabled flag or list membership.
 */
export type AppControlWrite = {
	/** Present when modifying; absent when creating. */
	id?: string;
	appControlId: string;
	name: string;
	action: "block" | "allow";
	/** True for a whole-class rule, in which case `studentStableIdV1` must be null. */
	isClassroomRule: boolean;
	studentStableIdV1: string | null;
};

export type AppControlClassroomRef = {
	/** Classroom ID used in the URL — `selectedClassroomStore.id`, not the stable ID. */
	classroomId: string;
	/** Body field identifying the classroom in storage — `selectedClassroomStore.stableIdV1`. */
	classroomStableIdV1: string;
	/** Body field naming the classroom source — `selectedClassroomStore.sourceId`. */
	classroomSourceName: string;
};

type CreateAppControlResponse = {
	ids: string[];
};

function appControlUrl(host: string, classroomId: string, path: "app-control" | "app-controls"): string {
	const deviceId = localStorage.getItem("DEVICE") ?? "";
	return `${host}/v1/devices/${encodeURIComponent(deviceId)}/classrooms/${encodeURIComponent(classroomId)}/${path}`;
}

/** Creates one app-control row and resolves with the server-assigned row ID. */
export async function createAppControl(
	classroom: AppControlClassroomRef,
	appControl: AppControlWrite,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<string> {
	const response = await fetch(appControlUrl(host, classroom.classroomId, "app-control"), {
		method: "PUT",
		headers: {
			...authHeaders,
		},
		body: JSON.stringify({
			...appControl,
			classroomStableIdV1: classroom.classroomStableIdV1,
			classroomSourceName: classroom.classroomSourceName,
		}),
	});

	if (!response.ok) {
		throw new Error(`Failed to create app control: ${response.status}`);
	}

	const body = (await response.json()) as CreateAppControlResponse;
	const id = body.ids?.[0];

	if (typeof id !== "string") {
		throw new Error("Failed to create app control: response contained no row ID");
	}

	return id;
}

export async function updateAppControl(
	classroom: AppControlClassroomRef,
	appControl: AppControlWrite & { id: string },
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<void> {
	const response = await fetch(appControlUrl(host, classroom.classroomId, "app-control"), {
		method: "POST",
		headers: {
			...authHeaders,
		},
		body: JSON.stringify({
			...appControl,
			classroomStableIdV1: classroom.classroomStableIdV1,
			classroomSourceName: classroom.classroomSourceName,
		}),
	});

	if (!response.ok) {
		throw new Error(`Failed to update app control: ${response.status}`);
	}
}

export async function deleteAppControls(
	classroom: AppControlClassroomRef,
	ids: string[],
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<void> {
	const response = await fetch(appControlUrl(host, classroom.classroomId, "app-controls"), {
		method: "DELETE",
		headers: {
			...authHeaders,
		},
		body: JSON.stringify({
			ids,
			classroomStableIdV1: classroom.classroomStableIdV1,
			classroomSourceName: classroom.classroomSourceName,
		}),
	});

	if (!response.ok) {
		throw new Error(`Failed to delete app controls: ${response.status}`);
	}
}

export type { NativeAppControlRule };
